<script setup lang="ts">
import { useId } from 'vue'

defineProps<{
  active: boolean
  reducedMotion: boolean
  compact?: boolean
}>()

const sceneId = useId().replaceAll(':', '')

function paint(name: string) {
  return `url(#${sceneId}-${name})`
}
</script>

<template>
  <!-- 认证翻新设备的小故事：检测、修复、归位、盖章。纯装饰，不承载额外信息。 -->
  <div
    class="buyer-scene relative size-full"
    :class="{ 'is-active': active, 'is-reduced': reducedMotion, 'is-compact': compact }"
    aria-hidden="true"
  >
    <svg class="size-full overflow-visible" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient :id="`${sceneId}-stage`" x1="52" y1="52" x2="352" y2="356" gradientUnits="userSpaceOnUse">
          <stop stop-color="white" stop-opacity=".105" />
          <stop offset=".52" stop-color="#A7F3D0" stop-opacity=".035" />
          <stop offset="1" stop-color="#022C22" stop-opacity=".16" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-metal`" x1="70" y1="70" x2="280" y2="350" gradientUnits="userSpaceOnUse">
          <stop stop-color="#D1FAE5" stop-opacity=".58" />
          <stop offset=".35" stop-color="#6EE7B7" stop-opacity=".19" />
          <stop offset=".68" stop-color="#ECFDF5" stop-opacity=".34" />
          <stop offset="1" stop-color="#064E3B" stop-opacity=".52" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-screen`" x1="130" y1="88" x2="264" y2="334" gradientUnits="userSpaceOnUse">
          <stop stop-color="#ECFDF5" stop-opacity=".22" />
          <stop offset=".42" stop-color="#A7F3D0" stop-opacity=".11" />
          <stop offset="1" stop-color="#022C22" stop-opacity=".28" />
        </linearGradient>
        <linearGradient :id="`${sceneId}-scan`" x1="42" y1="0" x2="358" y2="0" gradientUnits="userSpaceOnUse">
          <stop stop-color="#A7F3D0" stop-opacity="0" />
          <stop offset=".2" stop-color="#A7F3D0" stop-opacity=".45" />
          <stop offset=".5" stop-color="white" />
          <stop offset=".8" stop-color="#A7F3D0" stop-opacity=".45" />
          <stop offset="1" stop-color="#A7F3D0" stop-opacity="0" />
        </linearGradient>
        <filter :id="`${sceneId}-glow`" x="-40%" y="-100%" width="180%" height="300%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      <g class="stage-frame">
        <rect x="28" y="42" width="340" height="316" rx="44" :fill="paint('stage')" stroke="white" stroke-opacity=".17" stroke-width="1.5" />
        <path d="M56 76H92M304 76H340M56 324H92M304 324H340" stroke="#A7F3D0" stroke-opacity=".22" stroke-width="2" stroke-linecap="round" />
        <circle cx="52" cy="99" r="2.5" fill="#A7F3D0" fill-opacity=".34" />
        <circle cx="344" cy="300" r="2.5" fill="#A7F3D0" fill-opacity=".34" />
      </g>

      <g class="device-stack">
        <g class="device device-left">
          <rect x="48" y="151" width="108" height="184" rx="30" fill="#022C22" fill-opacity=".32" />
          <rect x="48" y="144" width="108" height="184" rx="30" :fill="paint('metal')" stroke="#D1FAE5" stroke-opacity=".46" stroke-width="2" />
          <rect x="55" y="152" width="94" height="168" rx="24" :fill="paint('screen')" stroke="white" stroke-opacity=".12" />
          <rect x="86" y="159" width="32" height="4" rx="2" fill="#D1FAE5" fill-opacity=".48" />
          <circle cx="126" cy="161" r="2.3" fill="#022C22" fill-opacity=".62" stroke="#A7F3D0" stroke-opacity=".45" />
          <path class="fine-detail" d="M69 185H116M69 196H132M69 285H108" stroke="#D1FAE5" stroke-opacity=".16" stroke-width="3" stroke-linecap="round" />
          <path d="M62 170C82 153 112 151 140 160L62 294V170Z" fill="white" fill-opacity=".055" />
          <circle class="blemish" cx="77" cy="237" r="5" />
        </g>

        <g class="device device-center">
          <rect x="124" y="80" width="152" height="270" rx="39" fill="#001B13" fill-opacity=".32" />
          <rect x="124" y="72" width="152" height="270" rx="39" :fill="paint('metal')" stroke="#D1FAE5" stroke-opacity=".62" stroke-width="2.2" />
          <rect x="132" y="80" width="136" height="254" rx="32" :fill="paint('screen')" stroke="white" stroke-opacity=".15" />
          <rect x="176" y="88" width="48" height="5" rx="2.5" fill="#D1FAE5" fill-opacity=".55" />
          <circle cx="231" cy="90.5" r="3" fill="#022C22" fill-opacity=".7" stroke="#A7F3D0" stroke-opacity=".45" />
          <circle class="fine-detail" cx="200" cy="180" r="46" fill="#A7F3D0" fill-opacity=".035" />
          <circle class="fine-detail" cx="200" cy="180" r="32" stroke="#A7F3D0" stroke-opacity=".1" stroke-width="1.5" />
          <path class="fine-detail" d="M169 270H231M182 283H218M190 315H210" stroke="#D1FAE5" stroke-opacity=".2" stroke-width="4" stroke-linecap="round" />
          <path d="M142 96C174 77 220 79 253 94L142 304V96Z" fill="white" fill-opacity=".065" />
          <circle class="blemish" cx="240" cy="222" r="6" />
        </g>

        <g class="device device-right">
          <rect x="275" y="169" width="94" height="164" rx="27" fill="#001B13" fill-opacity=".32" />
          <rect x="275" y="162" width="94" height="164" rx="27" :fill="paint('metal')" stroke="#D1FAE5" stroke-opacity=".42" stroke-width="2" />
          <rect x="281" y="169" width="82" height="150" rx="22" :fill="paint('screen')" stroke="white" stroke-opacity=".12" />
          <rect x="304" y="176" width="34" height="4" rx="2" fill="#D1FAE5" fill-opacity=".42" />
          <circle cx="344" cy="178" r="2.2" fill="#022C22" fill-opacity=".65" />
          <path class="fine-detail" d="M294 205H337M294 216H347M298 288H331" stroke="#D1FAE5" stroke-opacity=".15" stroke-width="3" stroke-linecap="round" />
          <path d="M287 182C305 169 331 168 355 178L287 295V182Z" fill="white" fill-opacity=".05" />
          <circle class="blemish" cx="301" cy="254" r="4.5" />
        </g>
      </g>

      <g class="scan-beam">
        <rect x="44" y="316" width="312" height="8" rx="4" fill="#6EE7B7" fill-opacity=".42" :filter="paint('glow')" />
        <rect x="42" y="319" width="316" height="2.5" rx="1.25" :fill="paint('scan')" />
        <circle cx="200" cy="320" r="4" fill="white" />
      </g>

      <g class="certification">
        <rect class="certification-ring" x="292" y="31" width="96" height="96" rx="29" stroke="#A7F3D0" stroke-opacity=".65" stroke-width="2" />
        <rect x="298" y="37" width="84" height="84" rx="25" fill="#5EE4B6" />
        <rect x="303" y="42" width="74" height="74" rx="21" stroke="white" stroke-opacity=".32" />
        <path d="M340 57C348 64 356 65 363 67V81C363 94 354 103 340 108C326 103 317 94 317 81V67C324 65 332 64 340 57Z" fill="#022C22" fill-opacity=".08" stroke="#022C22" stroke-width="4" stroke-linejoin="round" />
        <path d="M329 81L337 89L352 73" stroke="#022C22" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="369" cy="54" r="3" fill="white" fill-opacity=".62" />
        <path d="M311 59L318 52" stroke="white" stroke-opacity=".54" stroke-width="2" stroke-linecap="round" />
      </g>
    </svg>
  </div>
</template>

<style scoped>
.stage-frame {
  transform-origin: center;
}

.device {
  transform-box: fill-box;
  transform-origin: center;
}

.device-left {
  transform: rotate(-5deg);
}

.device-right {
  transform: rotate(5deg);
}

.blemish {
  fill: rgb(2 44 34 / 55%);
  stroke: rgb(209 250 229 / 58%);
  stroke-width: 1.4;
  opacity: 0;
}

.scan-beam,
.certification-ring {
  opacity: 0;
}

.certification {
  transform-box: fill-box;
  transform-origin: center;
}

.device-stack,
.certification {
  filter: drop-shadow(0 10px 10px rgb(0 27 19 / 24%));
}

.is-active .stage-frame {
  animation: buyer-stage-in 600ms cubic-bezier(.22, 1, .36, 1) 350ms both;
}

.is-active .device-left {
  animation: buyer-left-in 650ms cubic-bezier(.22, 1, .36, 1) 560ms both;
}

.is-active .device-center {
  animation: buyer-center-in 700ms cubic-bezier(.22, 1, .36, 1) 640ms both;
}

.is-active .device-right {
  animation: buyer-right-in 650ms cubic-bezier(.22, 1, .36, 1) 720ms both;
}

.is-active .blemish {
  animation: buyer-blemish-repair 900ms ease-out 820ms both;
}

.is-active .scan-beam {
  animation: buyer-scan 900ms cubic-bezier(.4, 0, .2, 1) 900ms both;
}

.is-active .certification {
  animation: buyer-stamp 520ms cubic-bezier(.34, 1.25, .64, 1) 1.42s both;
}

.is-active .certification-ring {
  animation: buyer-confirm-ring 580ms ease-out 1.68s both;
}

@keyframes buyer-stage-in {
  from { opacity: 0; transform: scale(.96); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes buyer-left-in {
  from { opacity: 0; transform: translate(-15px, 18px) rotate(-12deg); }
  to { opacity: 1; transform: translate(0) rotate(-5deg); }
}

@keyframes buyer-center-in {
  from { opacity: 0; transform: translateY(22px) scale(.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes buyer-right-in {
  from { opacity: 0; transform: translate(16px, 18px) rotate(12deg); }
  to { opacity: 1; transform: translate(0) rotate(5deg); }
}

@keyframes buyer-scan {
  0%, 100% { opacity: 0; transform: translateY(0) scaleX(.76); }
  16% { opacity: 1; }
  72% { opacity: .95; }
  86% { opacity: 0; }
  100% { transform: translateY(-226px) scaleX(1); }
}

@keyframes buyer-blemish-repair {
  0%, 35% { opacity: .85; transform: scale(1); }
  100% { opacity: 0; transform: scale(.15); }
}

@keyframes buyer-stamp {
  from { opacity: 0; transform: translateY(-18px) scale(.82) rotate(4deg); }
  70% { opacity: 1; transform: translateY(3px) scale(1.04) rotate(-1deg); }
  to { opacity: 1; transform: translateY(0) scale(1) rotate(0); }
}

@keyframes buyer-confirm-ring {
  from { opacity: .75; transform: scale(.76); }
  to { opacity: 0; transform: scale(1.28); }
}

.is-compact .fine-detail,
.is-compact .certification-ring {
  display: none;
}

@media (prefers-reduced-motion: reduce) {
  .buyer-scene *,
  .buyer-scene *::before,
  .buyer-scene *::after {
    animation: none !important;
  }

  .scan-beam,
  .certification-ring {
    display: none;
  }
}

.is-reduced *,
.is-reduced *::before,
.is-reduced *::after {
  animation: none !important;
}

.is-reduced .scan-beam,
.is-reduced .certification-ring {
  display: none;
}
</style>
